/**
 * @fileoverview Academy Expenses — manage expenses, expense heads, overall report with PDF download.
 */

import { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import InputAdornment from "@mui/material/InputAdornment";
import Alert from "@mui/material/Alert";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

import { academyService } from "api";
import FootballLoader from "components/FootballLoader";

const PAYMENT_MODES = ["Cash", "Online", "UPI", "Cheque", "NEFT", "IMPS"];

const TODAY = new Date().toISOString().slice(0, 10);
const FIRST_OF_MONTH = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);

const emptyExpense = {
  expenseHeadId: "", expenseDate: TODAY, amount: "", description: "",
  vendorName: "", receiptRef: "", paymentMode: "Cash", notes: "",
};

const emptyHead = { name: "", description: "", sortOrder: 0 };

function validateExpense(form) {
  const e = {};
  if (!form.expenseHeadId) e.expenseHeadId = "Category is required.";
  if (!form.expenseDate) e.expenseDate = "Date is required.";
  if (!form.amount || Number(form.amount) <= 0) e.amount = "Enter a valid amount.";
  if (!form.description.trim()) e.description = "Description is required.";
  return e;
}

// ── Generate PDF report ─────────────────────────────────────────────────────
async function downloadExpensePDF(report) {
  const { jsPDF } = await import("jspdf");
  const autoTable = (await import("jspdf-autotable")).default;
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const now = new Date().toLocaleString("en-IN");

  doc.setFontSize(16);
  doc.text("Academy Expenses Report", 14, 18);
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(`Period: ${report.from} to ${report.to}   |   Generated: ${now}`, 14, 25);
  doc.text(`Grand Total: ₹${Number(report.grandTotal).toLocaleString("en-IN")}`, 14, 30);

  // Summary by head
  doc.setFontSize(12);
  doc.setTextColor(0);
  doc.text("Summary by Category", 14, 40);
  autoTable(doc, {
    startY: 45,
    head: [["Category", "Count", "Total (₹)"]],
    body: (report.byHead ?? []).map((r) => [
      r.headName, r.count, `₹${Number(r.totalAmount).toLocaleString("en-IN")}`,
    ]),
    styles: { fontSize: 9, cellPadding: 2 },
    headStyles: { fillColor: [245, 124, 0], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [255, 248, 240] },
  });

  const afterSummary = doc.lastAutoTable.finalY + 8;
  doc.setFontSize(12);
  doc.text("Expense Details", 14, afterSummary);
  autoTable(doc, {
    startY: afterSummary + 4,
    head: [["#", "Date", "Category", "Description", "Vendor", "Receipt", "Mode", "Amount (₹)"]],
    body: (report.expenses ?? []).map((e, i) => [
      i + 1,
      String(e.expenseDate).slice(0, 10),
      e.expenseHeadName,
      e.description,
      e.vendorName ?? "",
      e.receiptRef ?? "",
      e.paymentMode ?? "",
      `₹${Number(e.amount).toLocaleString("en-IN")}`,
    ]),
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [26, 115, 232], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [245, 248, 255] },
  });

  doc.save(`academy-expenses-${report.from}-to-${report.to}.pdf`);
}

// ── Expense row ───────────────────────────────────────────────────────────
function ExpenseRow({ expense, onEdit, onDelete }) {
  return (
    <SoftBox display="flex" alignItems="center" py={1} px={2}
      sx={{ borderBottom: "1px solid #f5f5f5", minWidth: 700 }}>
      <SoftBox flex="0 0 100px">
        <SoftTypography variant="caption" color="text" sx={{ fontFamily: "monospace" }}>
          {String(expense.expenseDate).slice(0, 10)}
        </SoftTypography>
      </SoftBox>
      <SoftBox flex="0 0 120px">
        <Chip label={expense.expenseHeadName} size="small"
          sx={{ fontSize: "0.68rem", height: 20, backgroundColor: "rgba(245,124,0,0.1)", color: "#e65100" }} />
      </SoftBox>
      <SoftBox flex="1" sx={{ minWidth: 0 }}>
        <SoftTypography variant="caption" fontWeight="medium" color="dark">
          {expense.description}
        </SoftTypography>
        {expense.vendorName && (
          <SoftTypography variant="caption" color="text" display="block">{expense.vendorName}</SoftTypography>
        )}
      </SoftBox>
      <SoftBox flex="0 0 80px" textAlign="right">
        <SoftTypography variant="caption" fontWeight="bold" color="dark">
          ₹{Number(expense.amount).toLocaleString("en-IN")}
        </SoftTypography>
      </SoftBox>
      <SoftBox flex="0 0 80px" textAlign="center">
        <SoftTypography variant="caption" color="text">{expense.paymentMode ?? "—"}</SoftTypography>
      </SoftBox>
      <SoftBox flex="0 0 60px" textAlign="right" display="flex" gap={0.5}>
        <SoftButton variant="text" color="info" iconOnly size="small" onClick={() => onEdit(expense)}>
          <Icon>edit</Icon>
        </SoftButton>
        <SoftButton variant="text" color="error" iconOnly size="small" onClick={() => onDelete(expense)}>
          <Icon>delete</Icon>
        </SoftButton>
      </SoftBox>
    </SoftBox>
  );
}
ExpenseRow.propTypes = {
  expense: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

// ── Main ───────────────────────────────────────────────────────────────────
function AcademyExpenses() {
  const [tab, setTab] = useState(0);  // 0=Expenses 1=Report 2=Categories

  // Expenses
  const [expenses, setExpenses]         = useState([]);
  const [heads, setHeads]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [filterHead, setFilterHead]     = useState("");
  const [filterFrom, setFilterFrom]     = useState(FIRST_OF_MONTH);
  const [filterTo, setFilterTo]         = useState(TODAY);

  // Expense form dialog
  const [expDialogOpen, setExpDialogOpen] = useState(false);
  const [editingExp, setEditingExp]       = useState(null);
  const [expForm, setExpForm]             = useState(emptyExpense);
  const [expErrors, setExpErrors]         = useState({});
  const [expSaving, setExpSaving]         = useState(false);
  const [expSaveErr, setExpSaveErr]       = useState("");

  // Category (head) dialog
  const [headDialogOpen, setHeadDialogOpen] = useState(false);
  const [editingHead, setEditingHead]       = useState(null);
  const [headForm, setHeadForm]             = useState(emptyHead);
  const [headSaving, setHeadSaving]         = useState(false);
  const [headSaveErr, setHeadSaveErr]       = useState("");

  // Report
  const [report, setReport]             = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportFrom, setReportFrom]     = useState(FIRST_OF_MONTH);
  const [reportTo, setReportTo]         = useState(TODAY);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      academyService.getExpenses({ from: filterFrom, to: filterTo, headId: filterHead || undefined }),
      academyService.getExpenseHeads(),
    ]).then(([e, h]) => {
      setExpenses(Array.isArray(e) ? e : []);
      setHeads(Array.isArray(h) ? h : []);
    }).catch(console.error).finally(() => setLoading(false));
  }, [filterFrom, filterTo, filterHead]);

  useEffect(() => { load(); }, [load]);

  // Expense handlers
  const openAddExpense = () => {
    setEditingExp(null);
    setExpForm({ ...emptyExpense, expenseHeadId: heads[0]?.id ?? "" });
    setExpErrors({}); setExpSaveErr(""); setExpDialogOpen(true);
  };
  const openEditExpense = (exp) => {
    setEditingExp(exp);
    setExpForm({
      expenseHeadId: exp.expenseHeadId, expenseDate: String(exp.expenseDate).slice(0, 10),
      amount: exp.amount, description: exp.description, vendorName: exp.vendorName ?? "",
      receiptRef: exp.receiptRef ?? "", paymentMode: exp.paymentMode ?? "Cash", notes: exp.notes ?? "",
    });
    setExpErrors({}); setExpSaveErr(""); setExpDialogOpen(true);
  };
  const ef = (key) => (e) => {
    setExpForm((p) => ({ ...p, [key]: e.target.value }));
    setExpErrors((p) => ({ ...p, [key]: undefined }));
  };
  const handleSaveExpense = async () => {
    const errs = validateExpense(expForm);
    if (Object.keys(errs).length > 0) { setExpErrors(errs); return; }
    setExpSaving(true); setExpSaveErr("");
    try {
      const payload = { ...expForm, amount: Number(expForm.amount), expenseDate: expForm.expenseDate };
      if (editingExp) await academyService.updateExpense(editingExp.id, payload);
      else            await academyService.createExpense(payload);
      setExpDialogOpen(false); load();
    } catch (err) {
      setExpSaveErr(err?.detail ?? err?.title ?? "Save failed.");
    } finally { setExpSaving(false); }
  };
  const handleDeleteExpense = async (exp) => {
    if (!window.confirm(`Delete expense: ${exp.description}?`)) return;
    try {
      await academyService.deleteExpense(exp.id);
      load();
    } catch (err) {
      console.error(err);
    }
  };

  // Category (head) handlers
  const openAddHead = () => {
    setEditingHead(null); setHeadForm(emptyHead); setHeadSaveErr(""); setHeadDialogOpen(true);
  };
  const openEditHead = (h) => {
    setEditingHead(h);
    setHeadForm({ name: h.name, description: h.description ?? "", sortOrder: h.sortOrder });
    setHeadSaveErr(""); setHeadDialogOpen(true);
  };
  const hf = (key) => (e) => setHeadForm((p) => ({ ...p, [key]: e.target.value }));
  const handleSaveHead = async () => {
    if (!headForm.name.trim()) { setHeadSaveErr("Category name is required."); return; }
    setHeadSaving(true); setHeadSaveErr("");
    try {
      if (editingHead) await academyService.updateExpenseHead(editingHead.id, { ...headForm, isActive: true });
      else             await academyService.createExpenseHead(headForm);
      setHeadDialogOpen(false); load();
    } catch (err) {
      setHeadSaveErr(err?.detail ?? err?.title ?? "Save failed.");
    } finally { setHeadSaving(false); }
  };

  // Report
  const loadReport = async () => {
    setReportLoading(true);
    try {
      const data = await academyService.getExpenseReport({ from: reportFrom, to: reportTo });
      setReport(data);
    } catch (err) {
      console.error(err);
    } finally { setReportLoading(false); }
  };

  const totalThisMonth = expenses.reduce((s, e) => s + Number(e.amount), 0);
  const uniqueHeadsUsed = new Set(expenses.map((e) => e.expenseHeadId)).size;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <SoftBox>
            <SoftTypography variant="h4" fontWeight="bold" color="dark">Academy Expenses</SoftTypography>
            <SoftTypography variant="body2" color="text">Track, categorise, and report academy expenses</SoftTypography>
          </SoftBox>
          <SoftBox display="flex" gap={1}>
            <SoftButton variant="outlined" color="warning" startIcon={<Icon>category</Icon>} onClick={openAddHead}>
              Add Category
            </SoftButton>
            <SoftButton variant="gradient" color="info" startIcon={<Icon>add</Icon>} onClick={openAddExpense}>
              Add Expense
            </SoftButton>
          </SoftBox>
        </SoftBox>

        {/* KPIs */}
        <SoftBox mb={3}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard title={{ text: "Total Expenses" }} count={String(expenses.length)}
                percentage={{ color: "info", text: "in period" }} icon={{ color: "info", component: "receipt_long" }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard title={{ text: "Total Amount" }}
                count={`₹${totalThisMonth.toLocaleString("en-IN")}`}
                percentage={{ color: "error", text: "spent" }} icon={{ color: "error", component: "money_off" }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard title={{ text: "Categories Used" }} count={String(uniqueHeadsUsed)}
                percentage={{ color: "warning", text: "distinct" }} icon={{ color: "warning", component: "category" }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard title={{ text: "Expense Heads" }} count={String(heads.length)}
                percentage={{ color: "secondary", text: "configured" }} icon={{ color: "secondary", component: "tune" }} />
            </Grid>
          </Grid>
        </SoftBox>

        {/* Tabs */}
        <Card>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, pt: 1 }}>
            <Tab label="Expenses" icon={<Icon>receipt_long</Icon>} iconPosition="start" />
            <Tab label="Report" icon={<Icon>bar_chart</Icon>} iconPosition="start" />
            <Tab label="Categories" icon={<Icon>category</Icon>} iconPosition="start" />
          </Tabs>

          {/* ── Expenses Tab ── */}
          {tab === 0 && (
            <>
              <SoftBox p={2} display="flex" gap={2} flexWrap="wrap" alignItems="center">
                <TextField size="small" label="From" type="date" value={filterFrom}
                  onChange={(e) => setFilterFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 140 }} />
                <TextField size="small" label="To" type="date" value={filterTo}
                  onChange={(e) => setFilterTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 140 }} />
                <TextField select size="small" label="Category" value={filterHead}
                  onChange={(e) => setFilterHead(e.target.value)} sx={{ minWidth: 160 }}>
                  <MenuItem value="">All Categories</MenuItem>
                  {heads.map((h) => <MenuItem key={h.id} value={h.id}>{h.name}</MenuItem>)}
                </TextField>
                {filterHead && (
                  <Chip label="Clear" size="small" onDelete={() => setFilterHead("")} sx={{ height: 28 }} />
                )}
                <SoftTypography variant="caption" color="text" ml="auto">
                  {expenses.length} records · ₹{totalThisMonth.toLocaleString("en-IN")}
                </SoftTypography>
              </SoftBox>

              <SoftBox sx={{ overflowX: "auto" }}>
                <SoftBox display="flex" alignItems="center" px={2} py={1}
                  sx={{ borderBottom: "2px solid #f0f0f0", backgroundColor: "#fafafa", minWidth: 700 }}>
                  <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 100px" }}>DATE</SoftTypography>
                  <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 120px" }}>CATEGORY</SoftTypography>
                  <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: 1 }}>DESCRIPTION</SoftTypography>
                  <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 80px", textAlign: "right" }}>AMOUNT</SoftTypography>
                  <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 80px", textAlign: "center" }}>MODE</SoftTypography>
                  <SoftBox sx={{ flex: "0 0 60px" }} />
                </SoftBox>
                {loading ? (
                  <FootballLoader label="Loading expenses…" />
                ) : expenses.length === 0 ? (
                  <SoftBox p={4} textAlign="center">
                    <SoftTypography variant="caption" color="text">No expenses found for this period</SoftTypography>
                  </SoftBox>
                ) : (
                  expenses.map((exp) => (
                    <ExpenseRow key={exp.id} expense={exp} onEdit={openEditExpense} onDelete={handleDeleteExpense} />
                  ))
                )}
              </SoftBox>
            </>
          )}

          {/* ── Report Tab ── */}
          {tab === 1 && (
            <SoftBox p={3}>
              <SoftBox display="flex" gap={2} flexWrap="wrap" alignItems="flex-end" mb={3}>
                <TextField size="small" label="From" type="date" value={reportFrom}
                  onChange={(e) => setReportFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 140 }} />
                <TextField size="small" label="To" type="date" value={reportTo}
                  onChange={(e) => setReportTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 140 }} />
                <SoftButton variant="gradient" color="info" onClick={loadReport} disabled={reportLoading}>
                  {reportLoading ? "Loading…" : "Generate Report"}
                </SoftButton>
                {report && (
                  <SoftButton variant="outlined" color="info" startIcon={<Icon>download</Icon>}
                    onClick={() => downloadExpensePDF(report)}>
                    Download PDF
                  </SoftButton>
                )}
              </SoftBox>

              {report && (
                <>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Period: <strong>{String(report.from)} → {String(report.to)}</strong> &nbsp;·&nbsp;
                    Grand Total: <strong>₹{Number(report.grandTotal).toLocaleString("en-IN")}</strong>
                  </Alert>

                  {/* By head summary */}
                  <SoftTypography variant="h6" fontWeight="bold" color="dark" mb={1}>By Category</SoftTypography>
                  <SoftBox sx={{ overflowX: "auto", mb: 3 }}>
                    <SoftBox display="flex" px={1} py={0.5}
                      sx={{ borderBottom: "2px solid #f0f0f0", backgroundColor: "#fafafa" }}>
                      <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: 1 }}>CATEGORY</SoftTypography>
                      <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 80px", textAlign: "center" }}>COUNT</SoftTypography>
                      <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 120px", textAlign: "right" }}>TOTAL</SoftTypography>
                    </SoftBox>
                    {(report.byHead ?? []).map((row) => (
                      <SoftBox key={row.headName} display="flex" px={1} py={1}
                        sx={{ borderBottom: "1px solid #f5f5f5" }}>
                        <SoftTypography variant="caption" fontWeight="medium" color="dark" sx={{ flex: 1 }}>
                          {row.headName}
                        </SoftTypography>
                        <SoftTypography variant="caption" color="text" sx={{ flex: "0 0 80px", textAlign: "center" }}>
                          {row.count}
                        </SoftTypography>
                        <SoftTypography variant="caption" fontWeight="bold" color="dark" sx={{ flex: "0 0 120px", textAlign: "right" }}>
                          ₹{Number(row.totalAmount).toLocaleString("en-IN")}
                        </SoftTypography>
                      </SoftBox>
                    ))}
                  </SoftBox>

                  <Divider sx={{ my: 2 }} />

                  {/* Expense details */}
                  <SoftTypography variant="h6" fontWeight="bold" color="dark" mb={1}>All Expenses</SoftTypography>
                  <SoftBox sx={{ overflowX: "auto" }}>
                    <SoftBox display="flex" px={1} py={0.5}
                      sx={{ borderBottom: "2px solid #f0f0f0", backgroundColor: "#fafafa", minWidth: 600 }}>
                      <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 90px" }}>DATE</SoftTypography>
                      <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 110px" }}>CATEGORY</SoftTypography>
                      <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: 1 }}>DESCRIPTION</SoftTypography>
                      <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 100px", textAlign: "right" }}>AMOUNT</SoftTypography>
                    </SoftBox>
                    {(report.expenses ?? []).map((exp) => (
                      <SoftBox key={exp.id} display="flex" px={1} py={0.75}
                        sx={{ borderBottom: "1px solid #f5f5f5", minWidth: 600 }}>
                        <SoftTypography variant="caption" color="text" sx={{ flex: "0 0 90px" }}>
                          {String(exp.expenseDate).slice(0, 10)}
                        </SoftTypography>
                        <SoftTypography variant="caption" color="warning" sx={{ flex: "0 0 110px" }}>
                          {exp.expenseHeadName}
                        </SoftTypography>
                        <SoftTypography variant="caption" color="dark" sx={{ flex: 1 }}>
                          {exp.description}
                        </SoftTypography>
                        <SoftTypography variant="caption" fontWeight="bold" color="dark" sx={{ flex: "0 0 100px", textAlign: "right" }}>
                          ₹{Number(exp.amount).toLocaleString("en-IN")}
                        </SoftTypography>
                      </SoftBox>
                    ))}
                  </SoftBox>
                </>
              )}
            </SoftBox>
          )}

          {/* ── Categories Tab ── */}
          {tab === 2 && (
            <SoftBox p={3}>
              <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <SoftTypography variant="h6" fontWeight="bold" color="dark">Expense Categories</SoftTypography>
                <SoftButton variant="gradient" color="info" size="small" startIcon={<Icon>add</Icon>} onClick={openAddHead}>
                  Add Category
                </SoftButton>
              </SoftBox>
              {heads.length === 0 ? (
                <SoftTypography variant="caption" color="text">No categories yet. Add one to start tracking expenses.</SoftTypography>
              ) : (
                <Grid container spacing={2}>
                  {heads.map((h) => (
                    <Grid key={h.id} item xs={12} sm={6} md={4}>
                      <Card>
                        <SoftBox p={2} display="flex" justifyContent="space-between" alignItems="center">
                          <SoftBox>
                            <SoftTypography variant="button" fontWeight="bold" color="dark">{h.name}</SoftTypography>
                            {h.description && (
                              <SoftTypography variant="caption" color="text" display="block">{h.description}</SoftTypography>
                            )}
                          </SoftBox>
                          <SoftButton variant="text" color="info" size="small" onClick={() => openEditHead(h)}>
                            <Icon>edit</Icon>
                          </SoftButton>
                        </SoftBox>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </SoftBox>
          )}
        </Card>
      </SoftBox>
      <Footer company={{ href: "#", name: "Mohammedan SC" }} links={[]} />

      {/* ── Add/Edit Expense Dialog ── */}
      <Dialog open={expDialogOpen} onClose={() => setExpDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingExp ? "Edit Expense" : "Add Expense"}</DialogTitle>
        <DialogContent dividers>
          {expSaveErr && <Alert severity="error" sx={{ mb: 2 }}>{expSaveErr}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField select fullWidth required label="Category" value={expForm.expenseHeadId}
                onChange={ef("expenseHeadId")} size="small"
                error={Boolean(expErrors.expenseHeadId)} helperText={expErrors.expenseHeadId || " "}>
                <MenuItem value="" disabled><em>Select category…</em></MenuItem>
                {heads.map((h) => <MenuItem key={h.id} value={h.id}>{h.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth required label="Expense Date" type="date"
                value={expForm.expenseDate} onChange={ef("expenseDate")} size="small"
                InputLabelProps={{ shrink: true }}
                error={Boolean(expErrors.expenseDate)} helperText={expErrors.expenseDate || " "} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth required label="Amount (₹)" type="number"
                value={expForm.amount} onChange={ef("amount")} size="small"
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                error={Boolean(expErrors.amount)} helperText={expErrors.amount || " "} />
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label="Payment Mode" value={expForm.paymentMode}
                onChange={ef("paymentMode")} size="small" helperText=" ">
                {PAYMENT_MODES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth required label="Description" value={expForm.description}
                onChange={ef("description")} size="small"
                error={Boolean(expErrors.description)} helperText={expErrors.description || " "} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Vendor / Supplier" value={expForm.vendorName}
                onChange={ef("vendorName")} size="small" helperText=" " />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Receipt / Invoice No." value={expForm.receiptRef}
                onChange={ef("receiptRef")} size="small" helperText=" " />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Notes" value={expForm.notes}
                onChange={ef("notes")} size="small" multiline rows={2} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <SoftButton variant="text" color="secondary" onClick={() => setExpDialogOpen(false)}>Cancel</SoftButton>
          <SoftButton variant="gradient" color="info" onClick={handleSaveExpense} disabled={expSaving}>
            {expSaving ? "Saving…" : editingExp ? "Update" : "Add Expense"}
          </SoftButton>
        </DialogActions>
      </Dialog>

      {/* ── Add/Edit Category Dialog ── */}
      <Dialog open={headDialogOpen} onClose={() => setHeadDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editingHead ? "Edit Category" : "Add Expense Category"}</DialogTitle>
        <DialogContent dividers>
          {headSaveErr && <Alert severity="error" sx={{ mb: 2 }}>{headSaveErr}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth required label="Category Name" value={headForm.name}
                onChange={hf("name")} size="small" helperText=" " />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Description" value={headForm.description}
                onChange={hf("description")} size="small" multiline rows={2} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Sort Order" type="number" value={headForm.sortOrder}
                onChange={hf("sortOrder")} size="small" helperText=" " />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <SoftButton variant="text" color="secondary" onClick={() => setHeadDialogOpen(false)}>Cancel</SoftButton>
          <SoftButton variant="gradient" color="info" onClick={handleSaveHead} disabled={headSaving}>
            {headSaving ? "Saving…" : editingHead ? "Update" : "Add"}
          </SoftButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default AcademyExpenses;
